import React from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Stack, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Paper,
  Typography
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

interface MobileInputFormProps {
  ageValue: string;
  onAgeChange: (value: string) => void;
  heightInput: string;
  onHeightInputChange: (value: string) => void;
  heightUnit: 'cm' | 'in';
  onHeightUnitChange: (unit: 'cm' | 'in') => void;
  weightInput: string;
  onWeightInputChange: (value: string) => void;
  weightUnit: 'kg' | 'lb';
  onWeightUnitChange: (unit: 'kg' | 'lb') => void;
  onCalculate: () => void;
  onReset: () => void;
}

export const MobileInputForm: React.FC<MobileInputFormProps> = React.memo(({
  ageValue,
  onAgeChange,
  heightInput,
  onHeightInputChange,
  heightUnit,
  onHeightUnitChange,
  weightInput,
  onWeightInputChange,
  weightUnit,
  onWeightUnitChange,
  onCalculate,
  onReset
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (ageValue.trim()) {
      onCalculate();
    }
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 3, 
        mb: 2, 
        borderRadius: 2,
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'white'
      }}
    >
      <Typography 
        variant="h5" 
        sx={{ 
          mb: 3, 
          textAlign: 'center',
          fontWeight: 600,
          color: '#1976d2'
        }}
      >
        Peds Airway Calculator
      </Typography>
      
      <Typography 
        variant="body2" 
        sx={{ 
          mb: 3, 
          textAlign: 'center',
          color: '#666',
          fontStyle: 'italic'
        }}
      >
        For patients under 14 years of age
      </Typography>

      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
        <TextField
          label="Age"
          placeholder="e.g., 2 weeks, 18 months, 3 years"
          value={ageValue}
          onChange={(e) => onAgeChange(e.target.value)}
          fullWidth
          variant="outlined"
          size="medium"
          sx={{ 
            '& .MuiInputBase-input': { 
              fontSize: '1.1rem',
              padding: '14px'
            }
          }}
        />
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            label="Height (optional)"
            type="number"
            value={heightInput}
            onChange={(e) => onHeightInputChange(e.target.value)}
            variant="outlined"
            size="medium"
            sx={{ 
              flex: 1,
              '& .MuiInputBase-input': { 
                fontSize: '1.1rem',
                padding: '14px'
              }
            }}
          />
          <FormControl size="medium" sx={{ minWidth: 80 }}>
            <InputLabel>Unit</InputLabel>
            <Select
              value={heightUnit}
              onChange={(e) => onHeightUnitChange(e.target.value as 'cm' | 'in')}
              label="Unit"
              sx={{ 
                '& .MuiSelect-select': { 
                  fontSize: '1.1rem',
                  padding: '14px'
                }
              }}
            >
              <MenuItem value="cm">cm</MenuItem>
              <MenuItem value="in">in</MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            label="Weight (optional)"
            type="number"
            value={weightInput}
            onChange={(e) => onWeightInputChange(e.target.value)}
            variant="outlined"
            size="medium"
            sx={{ 
              flex: 1,
              '& .MuiInputBase-input': { 
                fontSize: '1.1rem',
                padding: '14px'
              }
            }}
          />
          <FormControl size="medium" sx={{ minWidth: 80 }}>
            <InputLabel>Unit</InputLabel>
            <Select
              value={weightUnit}
              onChange={(e) => onWeightUnitChange(e.target.value as 'kg' | 'lb')}
              label="Unit"
              sx={{ 
                '& .MuiSelect-select': { 
                  fontSize: '1.1rem',
                  padding: '14px'
                }
              }}
            >
              <MenuItem value="kg">kg</MenuItem>
              <MenuItem value="lb">lb</MenuItem>
            </Select>
          </FormControl>
        </Box>
        </Stack>

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={!ageValue.trim()}
            startIcon={<CalculateIcon />}
            fullWidth
            size="large"
            sx={{ 
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600
            }}
          >
            Calculate
          </Button>
          <Button
            variant="outlined"
            onClick={onReset}
            startIcon={<RestartAltIcon />}
            sx={{ 
              py: 1.5,
              fontSize: '1rem',
              minWidth: 100
            }}
          >
            Reset
          </Button>
        </Box>
      </form>
    </Paper>
  );
});